/**
 * 订单状态管理模型
 */

import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  getOrders,
  getOrderDetail,
  getOrderStatistics,
  updateOrderStatus,
  batchUpdateOrderStatus,
} from '@/services/orders';
import type {
  Order,
  OrderListParams,
  OrderStatistics,
  OrderStatusUpdateRequest,
  BatchOrderOperationRequest,
} from '@/services/orders.d';
import { debugLog } from '@/utils/debug';
import { extractItems, extractPagination } from '@/utils/apiAdapter';

export default function useOrdersModel() {
  // ============= 状态定义 =============

  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);
  
  const [orderStats, setOrderStats] = useState<OrderStatistics | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);

  // ============= 订单列表管理 =============

  /**
   * 获取订单列表
   */
  const fetchOrders = useCallback(async (params?: OrderListParams) => {
    setOrdersLoading(true);
    try {
      const response = await getOrders(params);
      if (response.success) {
        const ordersList = extractItems(response);
        const pagination = extractPagination(response);
        const total = pagination?.total || ordersList.length;

        setOrders(ordersList);
        setOrdersTotal(total);
      }
      return response;
    } catch (error) {
      debugLog.error('获取订单列表失败:', error);
      throw error;
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  /**
   * 获取单个订单详情
   */
  const fetchOrderDetail = useCallback(async (orderId: number) => {
    try {
      const response = await getOrderDetail(orderId);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || '获取订单详情失败');
    } catch (error) {
      debugLog.error('获取订单详情失败:', error);
      throw error;
    }
  }, []);

  /**
   * 添加订单到状态
   */
  const addOrder = useCallback((order: Order) => {
    setOrders((prev) => [order, ...prev]);
    setOrdersTotal((prev) => prev + 1);
  }, []);

  /**
   * 更新订单状态
   */
  const updateOrder = useCallback(
    (orderId: number, updates: Partial<Order>) => {
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, ...updates } : order,
        ),
      );
    },
    [],
  );

  /**
   * 删除订单
   */
  const removeOrder = useCallback((orderId: number) => {
    setOrders((prev) => prev.filter((order) => order.id !== orderId));
    setOrdersTotal((prev) => Math.max(0, prev - 1));
  }, []);

  // ============= 订单操作 =============

  /**
   * 更新订单状态
   */
  const updateSingleOrderStatus = useCallback(
    async (orderId: number, data: OrderStatusUpdateRequest) => {
      try {
        const response = await updateOrderStatus(orderId, data);
        if (response.success) {
          updateOrder(orderId, { status: data.status });
          message.success('订单状态更新成功');
          return response.data;
        }
        throw new Error(response.message || '更新失败');
      } catch (error) {
        debugLog.error('更新订单状态失败:', error);
        message.error('更新订单状态失败');
        throw error;
      }
    },
    [updateOrder],
  );

  /**
   * 批量更新订单状态
   */
  const batchUpdateOrders = useCallback(
    async (data: BatchOrderOperationRequest) => {
      try {
        const response = await batchUpdateOrderStatus(data);
        if (response.success) {
          // 更新本地状态
          if (data.operation === 'update_status' && data.params?.status) {
            data.order_ids.forEach((orderId) => {
              updateOrder(orderId, { status: data.params!.status });
            });
          }
          message.success(`批量操作成功，共处理 ${data.order_ids.length} 个订单`);
          return response;
        }
        throw new Error(response.message || '批量操作失败');
      } catch (error) {
        debugLog.error('批量更新订单失败:', error);
        message.error('批量操作失败');
        throw error;
      }
    },
    [updateOrder],
  );

  // ============= 统计数据管理 =============

  /**
   * 获取订单统计数据
   */
  const fetchOrderStats = useCallback(async (params?: {
    start_date?: string;
    end_date?: string;
    platform?: string;
  }) => {
    setStatsLoading(true);
    try {
      const response = await getOrderStatistics(params);
      if (response.success) {
        setOrderStats(response.data);
        return response.data;
      }
      throw new Error(response.message || '获取统计数据失败');
    } catch (error) {
      debugLog.error('获取订单统计失败:', error);
      throw error;
    } finally {
      setStatsLoading(false);
    }
  }, []);

  /**
   * 刷新统计数据
   */
  const refreshOrderStats = useCallback(async () => {
    try {
      await fetchOrderStats();
    } catch (error) {
      // 静默处理错误，不影响主要功能
      debugLog.warn('刷新统计数据失败:', error);
    }
  }, [fetchOrderStats]);

  // ============= 全局操作 =============

  /**
   * 刷新所有数据
   */
  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.allSettled([
        fetchOrders(),
        fetchOrderStats(),
      ]);
      setLastRefreshTime(new Date());
    } catch (error) {
      debugLog.error('刷新数据失败:', error);
      throw error;
    } finally {
      setRefreshing(false);
    }
  }, [fetchOrders, fetchOrderStats]);

  /**
   * 清除所有状态
   */
  const clearAll = useCallback(() => {
    setOrders([]);
    setOrdersTotal(0);
    setOrderStats(null);
    setLastRefreshTime(null);
  }, []);

  // ============= 工具函数 =============

  /**
   * 根据ID查找订单
   */
  const findOrderById = useCallback(
    (orderId: number) => {
      return orders.find((order) => order.id === orderId);
    },
    [orders],
  );

  /**
   * 获取订单计数信息
   */
  const getOrderCounts = useCallback(() => {
    const statusCounts = orders.reduce((acc, order) => {
      acc[order.status] = (acc[order.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const platformCounts = orders.reduce((acc, order) => {
      acc[order.platform] = (acc[order.platform] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: orders.length,
      byStatus: statusCounts,
      byPlatform: platformCounts,
    };
  }, [orders]);

  /**
   * 计算订单总金额
   */
  const getTotalAmount = useCallback(() => {
    return orders.reduce((total, order) => total + order.total_amount, 0);
  }, [orders]);

  // ============= 返回状态和方法 =============

  return {
    // 状态
    orders,
    ordersTotal,
    ordersLoading,
    orderStats,
    statsLoading,
    refreshing,
    lastRefreshTime,

    // 订单列表操作
    fetchOrders,
    fetchOrderDetail,
    addOrder,
    updateOrder,
    removeOrder,

    // 订单状态操作
    updateSingleOrderStatus,
    batchUpdateOrders,

    // 统计数据操作
    fetchOrderStats,
    refreshOrderStats,

    // 全局操作
    refreshAll,
    clearAll,

    // 工具函数
    findOrderById,
    getOrderCounts,
    getTotalAmount,
  };
}


